import glow from '@assets/jackpot-glow.png'
import { useGSAP } from '@gsap/react'
import { useConfigurationStore } from '@stores/configurationStore'
import { useJackpotStore } from '@stores/jackpotStore'
import {
    JackpotSuits,
    ViewportType,
    TOTAL_NUMBER_OF_CARDS,
    GameUIMessageType,
    JackpotStartSmallSuitMessage,
    GameType,
} from 'game-ui-types'
import gsap from 'gsap'
import MotionPathPlugin from 'gsap/MotionPathPlugin'
import React, { useEffect, useRef, useState } from 'react'
import { JackpotCardsConfigType } from './types'

gsap.registerPlugin(useGSAP, MotionPathPlugin)

interface JackpotMovingSuitProps {
    allConfigs: (gameType: GameType) => Record<JackpotSuits, JackpotCardsConfigType>
}

/**
 * JackpotMovingSuit Component
 *
 * Handles flying/moving suit when card is picked, flys over the panels and disappears. Shows glow when suit lands.
 */
const JackpotMovingSuit: React.FC<JackpotMovingSuitProps> = ({ allConfigs }) => {
    const viewport = useConfigurationStore((state) => state.viewport)
    const gameType = useConfigurationStore((state) => state.gameType)
    const viewportRef = useRef<ViewportType>(viewport)
    const stageDimensions = useConfigurationStore((state) => state.stageDimensions)
    const canvasDimensions = useConfigurationStore((state) => state.canvasDimensions)
    // Suits in store from backend
    const pickedSuits = useJackpotStore((state) => state.pickedSuits)
    const incrementPickedSuit = useJackpotStore((state) => state.incrementPickedSuit)
    // A local copy of picked suits since GSAP is async and the store might update differently to when GSAP ticks run
    // to find next position of the same suit
    const localPickedSuits = useRef<{ [key in JackpotSuits]: number }>({ ...pickedSuits })
    // A local array representation of the pickedSuits for rendering elements purposes only
    const [pickedSuitsArray, setPickedSuitsArray] = useState<(JackpotSuits | null)[]>(
        new Array(TOTAL_NUMBER_OF_CARDS).fill(null)
    )
    // A ref of the array for keeping track of the array while multiplie GSAP animations run
    const pickedSuitsArrayRef = useRef(pickedSuitsArray)
    // Keep track of which is the next element to animate
    const nextElementIndexRef = useRef(-1)
    const movingWrapperElement = useRef<HTMLDivElement>(null)
    const movingSuitElements = useRef<(HTMLDivElement | null)[]>([])
    const glowElements = useRef<(HTMLDivElement | null)[]>([])

    useEffect(() => {
        viewportRef.current = viewport
    }, [viewport])

    const getNextAvailableSuitPosition = (suit: JackpotSuits): { x: number; y: number } => {
        const nextIndex = Math.max(0, localPickedSuits.current[suit] - 1)

        return allConfigs(gameType)[suit][viewportRef.current].extended.smallSuits.positions[nextIndex]
    }

    const { contextSafe } = useGSAP(
        () => {
            const handleStartSuitFigureMove = contextSafe(
                ({
                    suit: s,
                    animationStartPoint,
                    tweensDuration,
                }: {
                    suit: JackpotSuits
                    animationStartPoint: { x: number; y: number }
                    tweensDuration: number
                }) => {
                    const suit = s.toUpperCase() as JackpotSuits

                    nextElementIndexRef.current = nextElementIndexRef.current + 1

                    const arr = [...pickedSuitsArrayRef.current]

                    arr[nextElementIndexRef.current] = suit
                    pickedSuitsArrayRef.current = arr
                    setPickedSuitsArray(arr)
                    localPickedSuits.current[suit] = localPickedSuits.current[suit] + 1

                    // Calculate the coordinate depending on difference between canvas and react size stages
                    if (stageDimensions[viewportRef.current].width !== canvasDimensions[viewportRef.current].width) {
                        animationStartPoint.x =
                            (animationStartPoint.x * stageDimensions[viewportRef.current].width) /
                            canvasDimensions[viewportRef.current].width
                    }

                    if (stageDimensions[viewportRef.current].height !== canvasDimensions[viewportRef.current].height) {
                        animationStartPoint.y =
                            (animationStartPoint.y * stageDimensions[viewportRef.current].height) /
                            canvasDimensions[viewportRef.current].height
                    }

                    const nextAvailableSuitPosition = getNextAvailableSuitPosition(suit)
                    const smallSuitDimensionOffset =
                        allConfigs(gameType)[suit][viewportRef.current].extended.smallSuits.dimensions / 2
                    const goToPosition = {
                        x: nextAvailableSuitPosition.x + smallSuitDimensionOffset,
                        y: nextAvailableSuitPosition.y + smallSuitDimensionOffset,
                    }

                    gsap.to(movingSuitElements.current[nextElementIndexRef.current], {
                        duration: tweensDuration,
                        motionPath: {
                            fromCurrent: false,
                            type: 'quadratic',
                            path: [animationStartPoint, goToPosition],
                            curviness: 0.1,
                        },
                        transformOrigin: 'center',
                        xPercent: -50,
                        yPercent: -50,
                        onStartParams: [nextElementIndexRef.current],
                        onStart: (index: number) => {
                            movingSuitElements.current[index]!.style.opacity = '1'
                        },
                    })

                    gsap.to(movingSuitElements.current[nextElementIndexRef.current], {
                        duration: tweensDuration,
                        motionPath: {
                            type: 'quadratic',
                            path: [{ scale: 1 }, { scale: 2.5 }, { scale: 0.3 }],
                            curviness: 1,
                        },
                        xPercent: -50,
                        yPercent: -50,
                        onCompleteParams: [nextElementIndexRef.current],
                        onComplete: (index: number) => {
                            incrementPickedSuit(suit)

                            movingSuitElements.current[index]!.style.opacity = '0'

                            if (!glowElements.current[index]) {
                                return
                            }

                            gsap.fromTo(
                                glowElements.current[index],
                                {
                                    opacity: 0,
                                    scale: 0,
                                    ...goToPosition,
                                    transformOrigin: 'center',
                                    xPercent: -50,
                                    yPercent: -50,
                                },
                                {
                                    opacity: 1,
                                    scale: 1,
                                    repeat: 1,
                                    yoyo: true,
                                    onStart: () => {
                                        glowElements.current[index]!.style.display = 'block'
                                    },
                                }
                            )
                        },
                    })
                }
            )

            const handleMessage = (event: MessageEvent) => {
                const rawData = event.data
                const type = rawData.type

                switch (type) {
                    case GameUIMessageType.JACKPOT_START_SMALL_SUIT: {
                        const data = rawData as JackpotStartSmallSuitMessage

                        handleStartSuitFigureMove(data)
                        break
                    }
                }
            }

            window.addEventListener('message', handleMessage, false)

            return () => {
                window.removeEventListener('message', handleMessage, false)
            }
        },
        { scope: movingWrapperElement }
    )

    return (
        <div ref={movingWrapperElement} className="moving-suit-wrapper relative z-[5]">
            {pickedSuitsArray.map((s) => (
                <>
                    <div
                        id={s ? `moving-suits-${s.toLowerCase()}` : undefined}
                        ref={(movingSuitElement) => {
                            movingSuitElements.current.push(movingSuitElement)
                        }}
                        className="moving-suit absolute top-0 left-0"
                        style={{ opacity: 0 }}
                    >
                        {s && <img src={allConfigs(gameType)[s][viewport].extended.smallSuits.images.pickedSuit} />}
                    </div>
                    <div
                        ref={(glowElement) => {
                            glowElements.current.push(glowElement)
                        }}
                        className="glow absolute top-0 left-0 hidden"
                        style={{ opacity: 0 }}
                    >
                        <img src={glow} />
                    </div>
                </>
            ))}
        </div>
    )
}

export default JackpotMovingSuit
